import React, { useRef, useState } from "react";
import { FaExternalLinkAlt, FaFileAlt, FaImage, FaImages, FaKey, FaPen, FaTrashAlt, FaVideo } from "react-icons/fa";
import { ErrorBox, Field, Options, UserOptions } from "./common";
import MediaGallery, { MediaPicker, MediaThumb } from "./MediaGallery";
import MediaUploader from "./MediaUploader";
import { canDelete, errorMessage, fmtDate, nullIfBlank, numOrNull } from "../dmApi";
import { usePrompt } from "../../work/WorkUi";

const EMPTY = { Content_Type: "Poster", Title: "", Body: "", Media_URL: "", Tool_Used: "", Prepared_By: "" };
const TOOLS = ["Canva", "Pollygen", "ChatGPT", "Gemini", "Photoshop", "CapCut", "Other"];

const ACTION_LABELS = {
  "Internal Review": "Send for internal review",
  "Client Review": "Send to client",
  Approved: "Approve",
  Rejected: "Reject",
  Draft: "Back to draft",
};

const statusKey = (status) => String(status || "").toLowerCase().replace(/\s+/g, "-");

function typeIcon(type) {
  const t = String(type || "").toLowerCase();
  if (/video|reel|short/.test(t)) return FaVideo;
  if (/poster|image|banner|carousel|creative/.test(t)) return FaImage;
  if (/keyword/.test(t)) return FaKey;
  return FaFileAlt;
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("") : "?";
}

function coverMedia(item) {
  const media = item.media || [];
  return media.find((m) => m.Url === item.Media_URL) || media[0] || null;
}

function ContentCard({ item, actions, onMove, onEdit, onDelete }) {
  const TypeIcon = typeIcon(item.Content_Type);
  const cover = coverMedia(item);
  const mediaCount = item.media?.length || 0;
  const reviewed = [
    item.Reviewed_By_Name ? `Reviewed by ${item.Reviewed_By_Name}` : null,
    item.Approved_On ? `Approved ${fmtDate(item.Approved_On)}` : null,
  ].filter(Boolean);

  return (
    <article className="dmp-citem">
      <div className="dmp-citem__top">
        <span className="dmp-citem__type">
          <TypeIcon aria-hidden="true" />
          {item.Content_Type}
        </span>
        {item.Tool_Used ? <span className="dmp-citem__tool">{item.Tool_Used}</span> : null}
        <div className="dmp-citem__icons">
          {item.Media_URL ? (
            <a
              href={item.Media_URL}
              target="_blank"
              rel="noreferrer"
              className="dmp-citem__icon"
              title="View media"
              aria-label="View media"
            >
              <FaExternalLinkAlt />
            </a>
          ) : null}
          <button type="button" className="dmp-citem__icon" title="Edit" aria-label="Edit" onClick={onEdit}>
            <FaPen />
          </button>
          {onDelete ? (
            <button
              type="button"
              className="dmp-citem__icon dmp-citem__icon--danger"
              title="Delete"
              aria-label="Delete"
              onClick={onDelete}
            >
              <FaTrashAlt />
            </button>
          ) : null}
        </div>
      </div>

      {cover ? (
        <div className="dmp-citem__media">
          <MediaThumb media={cover} />
          {mediaCount > 1 ? <span className="dmp-citem__media-count">1 / {mediaCount}</span> : null}
        </div>
      ) : null}
      <div className="dmp-citem__title">{item.Title}</div>
      {item.Body ? <p className="dmp-citem__body">{item.Body}</p> : null}
      {item.Review_Notes ? <div className="dmp-citem__note">{item.Review_Notes}</div> : null}

      {item.Prepared_By_Name || reviewed.length ? (
        <div className="dmp-citem__foot">
          {item.Prepared_By_Name ? (
            <span className="dmp-citem__by">
              <span className="dmp-citem__avatar" aria-hidden="true">
                {initials(item.Prepared_By_Name)}
              </span>
              {item.Prepared_By_Name}
            </span>
          ) : null}
          {reviewed.length ? <span className="dmp-citem__reviewed">{reviewed.join(" · ")}</span> : null}
        </div>
      ) : null}

      {actions.length ? (
        <div className="dmp-citem__actions">
          {actions.map((target) => (
            <button
              key={target}
              type="button"
              className={`dmp-citem__action dmp-citem__action--${statusKey(target)}`}
              onClick={() => onMove(target)}
            >
              {ACTION_LABELS[target] || target}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function ContentForm({ api, projectId, meta, initial, onSubmit, onCancel, onMediaChange, saving }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ""])),
    Prepared_By: initial?.Prepared_By ? String(initial.Prepared_By) : "",
  }));
  const [media, setMedia] = useState(() => initial?.media || []);
  const [mediaError, setMediaError] = useState("");
  const [picking, setPicking] = useState(false);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const contentId = initial?.Content_Id || null;
  const setCoverUrl = (url) => setForm((p) => ({ ...p, Media_URL: url || "" }));

  const uploaded = (item) => {
    setMedia((list) => [...list, item]);
    setForm((p) => (p.Media_URL ? p : { ...p, Media_URL: item.Url }));
    onMediaChange?.();
  };

  const makeCover = async (item) => {
    setMediaError("");
    try {
      if (contentId) await api.updateMedia(item.Media_Id, { Set_Cover: true });
      setCoverUrl(item.Url);
      onMediaChange?.();
    } catch (err) {
      setMediaError(errorMessage(err));
    }
  };

  const pickMedia = async (_url, item) => {
    setMediaError("");
    try {
      const linked = contentId ? await api.updateMedia(item.Media_Id, { Content_Id: contentId }) : null;
      uploaded({ ...item, ...(linked || {}) });
    } catch (err) {
      setMediaError(errorMessage(err));
    }
  };

  const unlinkMedia = async (item) => {
    setMediaError("");
    try {
      if (contentId) await api.updateMedia(item.Media_Id, { Content_Id: null });
      const rest = media.filter((m) => m.Media_Id !== item.Media_Id);
      setMedia(rest);
      if (form.Media_URL === item.Url) setCoverUrl(rest[0]?.Url);
      onMediaChange?.();
    } catch (err) {
      setMediaError(errorMessage(err));
    }
  };

  const removeMedia = async (item) => {
    if (!window.confirm(`Delete "${item.Original_Name || "this file"}" from storage?`)) return;
    setMediaError("");
    try {
      await api.deleteMedia(item.Media_Id);
      const rest = media.filter((m) => m.Media_Id !== item.Media_Id);
      setMedia(rest);
      if (form.Media_URL === item.Url) setCoverUrl(rest[0]?.Url);
      onMediaChange?.();
    } catch (err) {
      setMediaError(errorMessage(err));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit(
      {
        Content_Type: form.Content_Type,
        Title: form.Title.trim(),
        Body: nullIfBlank(form.Body),
        Media_URL: nullIfBlank(form.Media_URL.trim()),
        Tool_Used: nullIfBlank(form.Tool_Used),
        ...(form.Prepared_By ? { Prepared_By: numOrNull(form.Prepared_By) } : {}),
      },
      contentId ? [] : media.map((m) => m.Media_Id)
    );
  };
  const showMedia = form.Content_Type !== "Keywords" && form.Content_Type !== "Text";

  return (
    <form onSubmit={submit} className="dmp-card">
      <div className="dmp-form-grid">
        <Field label="Type">
          <select value={form.Content_Type} onChange={set("Content_Type")}>
            <Options values={meta?.content_types} />
          </select>
        </Field>
        <Field label="Title *">
          <input required value={form.Title} onChange={set("Title")} placeholder="eastern-pride-poster-1" />
        </Field>
        <Field label="Tool used">
          <input list="dmp-tools" value={form.Tool_Used} onChange={set("Tool_Used")} />
          <datalist id="dmp-tools">
            {TOOLS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Prepared by">
          <select value={form.Prepared_By} onChange={set("Prepared_By")}>
            <UserOptions users={meta?.users} blank="Me" />
          </select>
        </Field>
        {showMedia && api && projectId ? (
          <div className="dmp-field dmp-field--full dmp-content-media">
            Posters, images and videos
            <MediaUploader api={api} projectId={projectId} contentId={contentId} meta={meta} onUploaded={uploaded} compact />
            <div>
              <button type="button" className="dmp-chip-btn" onClick={() => setPicking((v) => !v)}>
                <FaImages aria-hidden="true" /> {picking ? "Hide media library" : "Choose from media library"}
              </button>
            </div>
            {picking ? (
              <MediaPicker
                api={api}
                projectId={projectId}
                unlinkedOnly
                excludeIds={media.map((m) => m.Media_Id)}
                title="Media library (files not used in other content)"
                emptyText="No unused files in this project. Upload them above or in the Media Library tab."
                onPick={pickMedia}
                onClose={() => setPicking(false)}
              />
            ) : null}
            <ErrorBox message={mediaError} />
            {media.length ? (
              <MediaGallery
                items={media}
                coverUrl={form.Media_URL}
                onSetCover={makeCover}
                onDelete={removeMedia}
                canDelete={(item) => canDelete(api, item)}
                renderExtra={(item) => (
                  <button type="button" className="dmp-link" onClick={() => unlinkMedia(item)}>
                    Remove from content
                  </button>
                )}
              />
            ) : null}
          </div>
        ) : null}
        <Field label={showMedia ? "Cover / external link (optional: Drive, Canva…)" : "Media URL (drive link)"} full>
          <input value={form.Media_URL} onChange={set("Media_URL")} placeholder="https://…" />
        </Field>
        <Field label={form.Content_Type === "Keywords" ? "Keywords" : "Text / caption"} full>
          <textarea rows={4} value={form.Body} onChange={set("Body")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </form>
  );
}

/** Content pipeline: Draft → Internal Review → Client Review → Approved (or Rejected). */
function ContentBoard({ api, meta, projectId, content, onChange }) {
  const [promptUi, askPrompt] = usePrompt();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const mediaDirty = useRef(false);
  const statuses = meta?.review_statuses || [];
  const transitions = meta?.review_transitions || {};

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await onChange();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const save = async (body, newMediaIds = []) => {
    setSaving(true);
    const ok = await run(async () => {
      if (editing !== "new") return api.updateContent(editing.Content_Id, body);
      const created = await api.createContent(projectId, body);
      for (const id of newMediaIds) {
        await api.updateMedia(id, { Content_Id: created.Content_Id });
      }
      return created;
    });
    setSaving(false);
    if (ok) {
      mediaDirty.current = false;
      setEditing(null);
    }
  };

  const cancel = () => {
    setEditing(null);
    if (mediaDirty.current) {
      mediaDirty.current = false;
      onChange();
    }
  };

  const move = async (item, target) => {
    let notes;
    if (target === "Rejected") {
      notes = await askPrompt({
        title: `Reject "${item.Title}"`,
        label: "What needs to change?",
        multiline: true,
        required: true,
        confirmLabel: "Reject",
      });
      if (!notes) return;
    }
    run(() => api.reviewContent(item.Content_Id, { Review_Status: target, Review_Notes: notes }));
  };

  const remove = (item) => {
    if (window.confirm(`Delete "${item.Title}"?`)) run(() => api.deleteContent(item.Content_Id));
  };

  return (
    <>
      {promptUi}
      {editing ? null : (
        <div className="dmp-header dmp-header--section" style={{ marginBottom: 10, justifyContent: "flex-end" }}>
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setEditing("new")}>
            Add content
          </button>
        </div>
      )}
      <ErrorBox message={error} />
      {editing ? (
        <ContentForm
          key={editing === "new" ? "new" : editing.Content_Id}
          api={api}
          projectId={projectId}
          meta={meta}
          initial={editing === "new" ? null : editing}
          saving={saving}
          onSubmit={save}
          onCancel={cancel}
          onMediaChange={() => {
            mediaDirty.current = true;
          }}
        />
      ) : null}
      <div className="dmp-board">
        {statuses.map((status) => {
          const items = content.filter((c) => c.Review_Status === status);
          return (
            <div
              key={status}
              className={`dmp-board__col dmp-board__col--${statusKey(status)}${items.length ? "" : " is-empty"}`}
            >
              <div className="dmp-board__head">
                <span className="dmp-board__name">
                  <span className="dmp-board__dot" aria-hidden="true" />
                  {status}
                </span>
                <span className="dmp-board__count">{items.length}</span>
              </div>
              {items.map((item) => (
                <ContentCard
                  key={item.Content_Id}
                  item={item}
                  actions={transitions[status] || []}
                  onMove={(target) => move(item, target)}
                  onEdit={() => setEditing(item)}
                  onDelete={canDelete(api, item) ? () => remove(item) : null}
                />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default ContentBoard;
